import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { discoveryApi, type CaregiverResult } from '../api/discovery';
import { EmptyState, Loading } from '../components/State';
import { isApiError } from '../lib/apiClient';
import './InvoicesPage.css';
import './BroadcastPage.css';
import './DiscoverPage.css';

export function DiscoverPage() {
  const qc = useQueryClient();
  const invalidateConnections = () => {
    qc.invalidateQueries({ queryKey: ['clients'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
    qc.invalidateQueries({ queryKey: ['pending-requests'] });
  };

  return (
    <div>
      <h1>Find families</h1>
      <PendingRequests onChange={invalidateConnections} />
      <ConnectWithCode onConnected={invalidateConnections} />
      <SearchCaregivers onRequested={invalidateConnections} />
    </div>
  );
}

// ── Requests waiting for the provider to accept ──────────────────────────────

function PendingRequests({ onChange }: { onChange: () => void }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['pending-requests'],
    queryFn: discoveryApi.pendingRequests,
  });

  const respond = useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) =>
      accept ? discoveryApi.acceptRequest(id) : discoveryApi.declineRequest(id),
    onSuccess: onChange,
  });

  if (isLoading || isError || !data || data.length === 0) return null;

  return (
    <section className="disc__section">
      <h2>Requests from families</h2>
      <div className="card inv__list">
        {data.map((r) => (
          <div key={r.connectionId} className="disc__row">
            <div className="disc__row-main">
              <div className="disc__row-title">
                {r.city || 'Unknown city'}
                {r.province ? `, ${r.province}` : ''}
              </div>
              <div className="muted disc__row-sub">
                {r.youthCount} youth{r.youthCount === 1 ? '' : 's'}
                {r.ageRange ? ` · ${r.ageRange}` : ''}
                {r.isIndigenous ? ' · Indigenous' : ''}
              </div>
            </div>
            <button
              className="btn btn--sm"
              disabled={respond.isPending}
              onClick={() => respond.mutate({ id: r.connectionId, accept: true })}
            >
              Accept
            </button>
            <button
              className="btn btn--ghost btn--sm"
              disabled={respond.isPending}
              onClick={() => respond.mutate({ id: r.connectionId, accept: false })}
            >
              Decline
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Connect using a caregiver access code ────────────────────────────────────

function ConnectWithCode({ onConnected }: { onConnected: () => void }) {
  const [code, setCode] = useState('');
  const m = useMutation({
    mutationFn: () => discoveryApi.connectWithCode(code.trim().toUpperCase()),
    onSuccess: () => {
      setCode('');
      onConnected();
    },
  });

  return (
    <section className="disc__section">
      <h2>Connect with a code</h2>
      <p className="muted disc__hint">
        Ask the caregiver for the access code shown in their app, then enter it here.
      </p>
      <div className="disc__inline">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Access code"
          maxLength={12}
        />
        <button className="btn" disabled={!code.trim() || m.isPending} onClick={() => m.mutate()}>
          {m.isPending ? 'Connecting…' : 'Connect'}
        </button>
      </div>
      {m.isError && (
        <p className="error-text">
          {isApiError(m.error) ? m.error.message : 'Could not connect with that code.'}
        </p>
      )}
      {m.isSuccess && <p className="bc__done">Request sent. The caregiver will confirm.</p>}
    </section>
  );
}

// ── Search discoverable caregiver households ─────────────────────────────────

function SearchCaregivers({ onRequested }: { onRequested: () => void }) {
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [indigenousOnly, setIndigenousOnly] = useState(false);
  const [results, setResults] = useState<CaregiverResult[] | null>(null);

  const search = useMutation({
    mutationFn: () => discoveryApi.search(city.trim(), province.trim(), indigenousOnly),
    onSuccess: (r) => setResults(r),
  });

  const request = useMutation({
    mutationFn: (householdId: string) => discoveryApi.requestConnection(householdId),
    onSuccess: (_r, householdId) => {
      setResults(
        (prev) =>
          prev?.map((x) =>
            x.householdId === householdId ? { ...x, connectionStatus: 'pending' } : x,
          ) ?? null,
      );
      onRequested();
    },
  });

  return (
    <section className="disc__section">
      <h2>Search by location</h2>
      <p className="muted disc__hint">
        Only caregiver households that opted in to be discoverable appear here.
      </p>
      <div className="inv__two">
        <div className="field">
          <label>City</label>
          <input value={city} onChange={(e) => setCity(e.target.value)} />
        </div>
        <div className="field">
          <label>Province</label>
          <input value={province} onChange={(e) => setProvince(e.target.value)} />
        </div>
      </div>
      <label className="bc__check">
        <input
          type="checkbox"
          checked={indigenousOnly}
          onChange={(e) => setIndigenousOnly(e.target.checked)}
        />
        Indigenous families only
      </label>
      <button
        className="btn"
        disabled={(!city.trim() && !province.trim()) || search.isPending}
        onClick={() => search.mutate()}
      >
        {search.isPending ? 'Searching…' : 'Search'}
      </button>

      {search.isError && (
        <p className="error-text">
          {isApiError(search.error) ? search.error.message : 'Search failed.'}
        </p>
      )}

      {search.isPending && <Loading />}

      {results && results.length === 0 && (
        <div style={{ marginTop: 16 }}>
          <EmptyState text="No discoverable families matched. Try a different city or province." />
        </div>
      )}

      {results && results.length > 0 && (
        <div className="card inv__list" style={{ marginTop: 16 }}>
          {results.map((r) => (
            <div key={r.householdId} className="disc__row">
              <div className="disc__row-main">
                <div className="disc__row-title">
                  {r.city || 'Unknown city'}
                  {r.province ? `, ${r.province}` : ''}
                </div>
                <div className="muted disc__row-sub">
                  {r.youthCount} youth{r.youthCount === 1 ? '' : 's'}
                  {r.ageRange ? ` · ${r.ageRange}` : ''}
                  {r.isIndigenous ? ' · Indigenous' : ''}
                </div>
              </div>
              {r.connectionStatus === 'active' ? (
                <span className="inv__pill inv__pill--paid">Connected</span>
              ) : r.connectionStatus ? (
                <span className="inv__pill inv__pill--draft">Pending</span>
              ) : (
                <button
                  className="btn btn--sm"
                  disabled={request.isPending}
                  onClick={() => request.mutate(r.householdId)}
                >
                  Request
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
